(() => {
  if (window.customElements.get('sc-mega-menu-controller')) {
    return;
  }

  const ETRANSLATE_SELECTOR = '#etranslate-language-embedded-selector-theme2';
  const PAGE_SCROLL_LOCK_CLASS = 'sc-mega-menu-is-open';
  const desktopMedia = window.matchMedia('(min-width: 768px)');

  const syncPageScrollLock = () => {
    const hasOpenMegaMenu = desktopMedia.matches && Array.from(
      document.querySelectorAll('desktop-navigation .sc-mega-menu-trigger > [aria-controls][aria-expanded="true"]')
    ).some((trigger) => {
      const dropdownId = trigger.getAttribute('aria-controls');
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null;
      return dropdown?.classList.contains('sc-mega-menu') && !dropdown.hidden;
    });

    const body = document.body;

    if (!body || body.classList.contains(PAGE_SCROLL_LOCK_CLASS) === hasOpenMegaMenu) {
      return;
    }

    if (hasOpenMegaMenu) {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const bodyPaddingInlineEnd = Number.parseFloat(window.getComputedStyle(body).paddingInlineEnd) || 0;

      body.style.setProperty('--sc-mega-menu-body-padding-inline-end', `${bodyPaddingInlineEnd}px`);
      body.style.setProperty('--sc-mega-menu-scrollbar-compensation', `${scrollbarWidth}px`);
      body.classList.add(PAGE_SCROLL_LOCK_CLASS);
      return;
    }

    body.classList.remove(PAGE_SCROLL_LOCK_CLASS);
    body.style.removeProperty('--sc-mega-menu-body-padding-inline-end');
    body.style.removeProperty('--sc-mega-menu-scrollbar-compensation');
  };

  const getMenuElements = (menuItem) => {
    const trigger = menuItem?.querySelector(':scope > [aria-controls]');
    const dropdownId = trigger?.getAttribute('aria-controls');
    const dropdown = dropdownId
      ? menuItem.querySelector(`#${CSS.escape(dropdownId)}`)
      : null;

    if (!trigger || !dropdown) {
      return null;
    }

    return { trigger, dropdown };
  };

  const getEditorBlockId = (element) => {
    try {
      return JSON.parse(element.getAttribute('data-shopify-editor-block')).id;
    } catch (error) {
      return null;
    }
  };

  class SCMegaMenuController extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.eventsController = new AbortController();
      this.initialize();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    initialize() {
      this.headerSection = this.closest('.shopify-section--header');

      if (!this.headerSection) {
        return;
      }

      this.desktopNavigation = this.headerSection.querySelector('desktop-navigation');
      desktopMedia.addEventListener('change', syncPageScrollLock, { signal: this.eventsController.signal });
      this.initializeDesktopNavigation();
      this.initializeViewportOffset();
      this.initializeThemeEditor();
    }

    initializeDesktopNavigation() {
      const navigation = this.desktopNavigation;

      if (!navigation || typeof navigation.openDropdown !== 'function' || typeof navigation.closeDropdown !== 'function') {
        return;
      }

      const { signal } = this.eventsController;
      const openingTimers = new WeakMap();
      const closingTimers = new WeakMap();
      let nativeTransitionTimer = null;
      const triggers = Array.from(navigation.querySelectorAll('.sc-mega-menu-trigger'));
      const nativeOpenDropdown = navigation.openDropdown;
      const nativeCloseDropdown = navigation.closeDropdown;

      const clearNativeTransitionTimer = () => {
        if (nativeTransitionTimer !== null) {
          window.clearTimeout(nativeTransitionTimer);
          nativeTransitionTimer = null;
        }
      };

      this.navigationBridge = {
        navigation,
        nativeOpenDropdown,
        nativeCloseDropdown,
        openingTimers,
        closingTimers,
        triggers,
        clearNativeTransitionTimer,
      };

      const clearTimer = (timers, menuItem) => {
        const timer = timers.get(menuItem);

        if (timer) {
          window.clearTimeout(timer);
          timers.delete(menuItem);
        }
      };

      const closeNow = (menuItem, force = false) => {
        if (!menuItem || (!force && menuItem.classList.contains('is-editor-pinned'))) {
          return;
        }

        clearTimer(openingTimers, menuItem);
        clearTimer(closingTimers, menuItem);

        const menuElements = getMenuElements(menuItem);

        if (!menuElements || menuElements.trigger.getAttribute('aria-expanded') !== 'true') {
          return;
        }

        nativeCloseDropdown.call(navigation, menuItem);
        requestAnimationFrame(syncPageScrollLock);
      };

      const openSCMenu = (menuItem) => {
        const menuElements = getMenuElements(menuItem);

        if (!menuElements) {
          return;
        }

        clearTimer(closingTimers, menuItem);

        clearTimer(openingTimers, menuItem);

        const openingTimer = window.setTimeout(() => {
          openingTimers.delete(menuItem);

          if (menuElements.trigger.getAttribute('aria-expanded') === 'true') {
            return;
          }

          navigation.querySelectorAll('.sc-mega-menu-trigger').forEach((candidate) => {
            const candidateTrigger = candidate.querySelector(':scope > [aria-controls]');

            if (candidate !== menuItem && candidateTrigger?.getAttribute('aria-expanded') === 'true') {
              closeNow(candidate);
            }
          });

          navigation.currentMegaMenu = menuElements.dropdown;
          menuElements.dropdown.classList.remove('is-closing');
          menuElements.trigger.setAttribute('aria-expanded', 'true');
          menuElements.dropdown.removeAttribute('hidden');
          syncPageScrollLock();
          navigation.dispatchEvent(new CustomEvent('desktop-nav:dropdown:open', { bubbles: true }));
        }, 100);

        openingTimers.set(menuItem, openingTimer);
      };

      navigation.openDropdown = function (menuItem) {
        if (menuItem?.classList.contains('sc-mega-menu-trigger')) {
          openSCMenu(menuItem);
          return;
        }

        nativeOpenDropdown.call(this, menuItem);
      };

      navigation.closeDropdown = function (menuItem) {
        if (menuItem?.classList.contains('sc-mega-menu-trigger')) {
          closeNow(menuItem);
          return;
        }

        nativeCloseDropdown.call(this, menuItem);
      };

      triggers.forEach((menuItem) => {
        menuItem.addEventListener('mouseenter', () => {
          clearNativeTransitionTimer();
          clearTimer(closingTimers, menuItem);
        }, { signal });

        menuItem.addEventListener('mouseleave', (event) => {
          clearTimer(openingTimers, menuItem);

          if (event.relatedTarget === null || menuItem.classList.contains('is-editor-pinned')) {
            return;
          }

          clearTimer(closingTimers, menuItem);
          closingTimers.set(menuItem, window.setTimeout(() => {
            closingTimers.delete(menuItem);

            if (!menuItem.matches(':hover')) {
              closeNow(menuItem);
            }
          }, 500));
        }, { signal });
      });

      navigation.addEventListener('pointerover', (event) => {
        const nextMenuItem = event.target instanceof Element
          ? event.target.closest('.has-dropdown')
          : null;

        clearNativeTransitionTimer();

        if (!nextMenuItem || nextMenuItem.classList.contains('sc-mega-menu-trigger')) {
          return;
        }

        nativeTransitionTimer = window.setTimeout(() => {
          nativeTransitionTimer = null;
          navigation.querySelectorAll('.sc-mega-menu-trigger').forEach((menuItem) => closeNow(menuItem));
        }, 150);
      }, { signal });

      document.documentElement.addEventListener('mouseleave', () => {
        navigation.querySelectorAll('.sc-mega-menu-trigger').forEach((menuItem) => closeNow(menuItem));
      }, { signal });
    }

    initializeViewportOffset() {
      const scMegaMenu = this.desktopNavigation?.querySelector('.sc-mega-menu');

      if (!scMegaMenu || !this.headerSection) {
        return;
      }

      const { signal } = this.eventsController;
      let eTranslateElement = null;
      let viewportUpdateFrame = null;
      let discoveryTimer = null;
      let discoveryAttempts = 0;
      const resizeObserver = 'ResizeObserver' in window
        ? new ResizeObserver(() => requestViewportOffsetUpdate())
        : null;

      const updateViewportOffset = () => {
        viewportUpdateFrame = null;

        if (!scMegaMenu.isConnected) {
          return;
        }

        const viewportOffset = Math.max(0, scMegaMenu.getBoundingClientRect().top);
        this.headerSection.style.setProperty('--sc-mega-menu-viewport-offset', `${viewportOffset}px`);
      };

      function requestViewportOffsetUpdate() {
        if (viewportUpdateFrame === null) {
          viewportUpdateFrame = requestAnimationFrame(updateViewportOffset);
        }
      }

      const discoverETranslate = () => {
        discoveryTimer = null;
        const nextElement = document.querySelector(ETRANSLATE_SELECTOR);

        if (nextElement && nextElement !== eTranslateElement) {
          if (eTranslateElement) {
            resizeObserver?.unobserve(eTranslateElement);
          }

          eTranslateElement = nextElement;
          resizeObserver?.observe(eTranslateElement);
          requestViewportOffsetUpdate();
          return;
        }

        if (!nextElement && discoveryAttempts < 60) {
          discoveryAttempts += 1;
          discoveryTimer = window.setTimeout(discoverETranslate, 500);
        }
      };

      resizeObserver?.observe(this.headerSection);

      const announcementBar = document.querySelector('.shopify-section--announcement-bar');

      if (announcementBar) {
        resizeObserver?.observe(announcementBar);
      }

      discoverETranslate();
      requestViewportOffsetUpdate();
      window.addEventListener('resize', requestViewportOffsetUpdate, { signal });
      window.addEventListener('scroll', requestViewportOffsetUpdate, { passive: true, signal });
      window.addEventListener('load', requestViewportOffsetUpdate, { once: true, signal });
      document.fonts?.ready.then(() => {
        if (!signal.aborted) {
          requestViewportOffsetUpdate();
        }
      });

      this.viewportCleanup = () => {
        if (viewportUpdateFrame !== null) {
          cancelAnimationFrame(viewportUpdateFrame);
        }

        if (discoveryTimer !== null) {
          window.clearTimeout(discoveryTimer);
        }

        resizeObserver?.disconnect();
        this.headerSection?.style.removeProperty('--sc-mega-menu-viewport-offset');
      };
    }

    initializeThemeEditor() {
      const navigation = this.desktopNavigation;

      if (!window.Shopify?.designMode || !navigation) {
        return;
      }

      const { signal } = this.eventsController;
      let pinnedMenuItem = null;

      const getSCMenuItem = (target, blockId) => {
        const closestMenuItem = target instanceof Element
          ? target.closest('desktop-navigation .sc-mega-menu-trigger, desktop-navigation .sc-menu-trigger--simple')
          : null;

        if (closestMenuItem || !blockId) {
          return closestMenuItem;
        }

        return Array.from(navigation.querySelectorAll('.sc-mega-menu-trigger, .sc-menu-trigger--simple')).find((menuItem) => {
          if (menuItem.dataset.menuBlockId === blockId) {
            return true;
          }

          return Array.from(menuItem.querySelectorAll('[data-shopify-editor-block]')).some((element) => {
            return getEditorBlockId(element) === blockId;
          });
        });
      };

      const setMenuState = (menuItem, isOpen) => {
        const menuElements = getMenuElements(menuItem);

        if (!menuElements) {
          return;
        }

        menuElements.trigger.setAttribute('aria-expanded', String(isOpen));
        menuElements.dropdown.toggleAttribute('hidden', !isOpen);
        menuElements.dropdown.classList.remove('is-closing');
        menuItem.classList.toggle('is-editor-pinned', isOpen);
        syncPageScrollLock();
      };

      const dispatchMenuState = (isOpen) => {
        navigation.dispatchEvent(
          new CustomEvent(`desktop-nav:dropdown:${isOpen ? 'open' : 'close'}`, { bubbles: true })
        );
      };

      const closePinnedMenu = () => {
        if (!pinnedMenuItem) {
          return;
        }

        const previousMenuItem = pinnedMenuItem;
        pinnedMenuItem = null;
        setMenuState(previousMenuItem, false);
        dispatchMenuState(false);
      };

      const openPinnedMenu = (menuItem) => {
        if (pinnedMenuItem === menuItem) {
          setMenuState(menuItem, true);
          return;
        }

        const previousMenuItem = pinnedMenuItem;
        pinnedMenuItem = menuItem;

        navigation.querySelectorAll('.header__linklist-item.has-dropdown').forEach((item) => {
          if (item !== menuItem) {
            setMenuState(item, false);
          }
        });

        previousMenuItem?.classList.remove('is-editor-pinned');
        setMenuState(menuItem, true);
        dispatchMenuState(true);
      };

      this.editorCleanup = () => {
        if (pinnedMenuItem) {
          pinnedMenuItem.classList.remove('is-editor-pinned');
        }

        pinnedMenuItem = null;
      };

      document.addEventListener('shopify:block:select', (event) => {
        const selectedMenuItem = getSCMenuItem(event.target, event.detail?.blockId);

        if (selectedMenuItem) {
          openPinnedMenu(selectedMenuItem);
        } else {
          closePinnedMenu();
        }
      }, { capture: true, signal });

      const stopNativeBlockHandling = (event) => {
        if (getSCMenuItem(event.target, event.detail?.blockId)) {
          event.stopImmediatePropagation();
        }
      };

      navigation.addEventListener('shopify:block:select', stopNativeBlockHandling, {
        capture: true,
        signal,
      });
      navigation.addEventListener('shopify:block:deselect', stopNativeBlockHandling, {
        capture: true,
        signal,
      });

      document.documentElement.addEventListener('mouseenter', (event) => {
        const hoveredMenuItem = event.target instanceof Element
          ? event.target.closest('desktop-navigation .has-dropdown')
          : null;

        if (pinnedMenuItem && hoveredMenuItem && hoveredMenuItem !== pinnedMenuItem) {
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });

      document.documentElement.addEventListener('mouseleave', (event) => {
        const leftMenuItem = getSCMenuItem(event.target);

        if (pinnedMenuItem && (event.target === document.documentElement || leftMenuItem === pinnedMenuItem)) {
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
    }

    cleanup() {
      this.eventsController?.abort();
      this.viewportCleanup?.();
      this.editorCleanup?.();

      const bridge = this.navigationBridge;

      if (bridge) {
        bridge.clearNativeTransitionTimer?.();

        bridge.triggers.forEach((menuItem) => {
          const openingTimer = bridge.openingTimers.get(menuItem);
          const closingTimer = bridge.closingTimers.get(menuItem);

          if (openingTimer) {
            window.clearTimeout(openingTimer);
          }

          if (closingTimer) {
            window.clearTimeout(closingTimer);
          }
        });

        bridge.navigation.openDropdown = bridge.nativeOpenDropdown;
        bridge.navigation.closeDropdown = bridge.nativeCloseDropdown;
      }

      this.initialized = false;
      this.navigationBridge = null;
      this.viewportCleanup = null;
      this.editorCleanup = null;
      requestAnimationFrame(syncPageScrollLock);
    }
  }

  window.customElements.define('sc-mega-menu-controller', SCMegaMenuController);
})();
